import SkillNodeGFX from "./SkillNodeGFX"
import SkillNode from "./SkillNode"

class SkillNodeTraverseState extends SkillNode {
	prev: SkillNodeTraverseState
	path?: any[]
	steps?: number
}


class SkillBoardTraverser {
    permanentNodeIds: number[] = [1]

    constructor() {}

    chainAllocation(skillNodeGFX: SkillNodeGFX, allocatedNodeIds: number[]): SkillNodeGFX[] {
        if (allocatedNodeIds.includes(skillNodeGFX.node.skillId)) {
            return []
        }

        const allocationChain: SkillNodeGFX[] = []
        const startingNode = Object.assign({}, skillNodeGFX.node) as SkillNodeTraverseState

        startingNode.prev = {
			name: 'TerminalNode',
            prev: {
                'skillId': -1
            },
            path: []
        } as SkillNodeTraverseState

        let timeout = 500       // This search can lead in circular paths.  Shouldn't ever time out, but...
        let goalFound = false
        const queue: SkillNodeTraverseState[] = []
        queue.push(startingNode)

        while (timeout > 0 && !goalFound) {
            const currentNode = queue.shift()

            const nextGuys: SkillNodeTraverseState[] = []

            currentNode.links.forEach((link) => {
                if (!goalFound) {
                    currentNode.path = Object.assign([], currentNode.prev.path)
                    currentNode.path.push(currentNode.gfx)

                    if (allocatedNodeIds.includes(link.skillId)) {
                        allocationChain.push(...currentNode.path)
                        goalFound = true
                    }
                    
                    if (!goalFound && link.skillId !== currentNode.prev.skillId) {
                        const nextNode = Object.assign({}, link) as SkillNodeTraverseState
                        nextNode.prev = Object.assign({}, currentNode) as SkillNodeTraverseState

                        if (!!nextNode) {
                            nextGuys.push(nextNode)
                        }
                    }
                }
            })

            nextGuys.forEach((node) => queue.push(node))

            timeout--
        }

        return allocationChain.reverse()
    }

    chainDeallocation(skillNodeGFX: SkillNodeGFX, userAllocatedNodeIds: number[]): SkillNodeGFX[] {
        if (this.permanentNodeIds.includes(skillNodeGFX.node.skillId)) {
            return []
        }

        const self = this
        const nodesToDeallocate: Set<SkillNodeGFX> = new Set<SkillNodeGFX>()
        const allocatedNodeIds = Object.assign([], userAllocatedNodeIds)
        const nodesToChain: SkillNodeGFX[] = []

        const index = allocatedNodeIds.indexOf(skillNodeGFX.node.skillId)
        if (index >= 0) {
            allocatedNodeIds.splice(index, 1)
        }
        nodesToDeallocate.add(skillNodeGFX)

        skillNodeGFX.links.forEach((link) => {
            if (link.node.skillId !== skillNodeGFX.node.skillId 
                    && allocatedNodeIds.includes(link.node.skillId)
                    && !self.permanentNodeIds.includes(link.node.skillId)
                ) {
                // If, after deallocating the current node (skillNodeGFX), the node has no allocated links, deallocate it.
                //      This should take care of all orphaned leaf nodes
                if (self.hasNoAllocatedLinks(link.node, allocatedNodeIds)) {
                    nodesToDeallocate.add(link)
                }

                // Get whether the node has dependencies, and the number of steps to the center if navigable
                const deallocationCheckResults = self.checkDeallocation(link.node, allocatedNodeIds)

                // If the node has dependencies and has no path to the center, chain it.
                //      This should take care of all orphaned branch nodes
                if (!deallocationCheckResults.allow && deallocationCheckResults.steps === -1) {
                    // Add to a queue rather than recursing now: required for chaining via Breadth-first-search
                    nodesToChain.push(link)
                }
            }
        })

        nodesToChain.forEach(node => {
            self.chainDeallocation(node, allocatedNodeIds)
                .forEach(deallocMe => nodesToDeallocate.add(deallocMe))
        })

        return [...nodesToDeallocate]
    }

    checkDeallocation(skillNode: SkillNode, userAllocatedNodeIds: number[]): { allow: boolean, steps: number } {
        if (this.permanentNodeIds.includes(skillNode.skillId)) {
            return {allow: false, steps: -1}
        }

        const self = this
        const startingNode = Object.assign({}, skillNode) as SkillNodeTraverseState
        const allocatedNodeIds = Object.assign([], userAllocatedNodeIds)
        allocatedNodeIds.splice(allocatedNodeIds.indexOf(startingNode.skillId), 1)

        const nodesToCheck: SkillNodeTraverseState[] = []

        startingNode.links.forEach((link) => {
            if (allocatedNodeIds.includes(link.skillId) && !self.permanentNodeIds.includes(link.skillId)) {
                const node = Object.assign({}, link) as SkillNodeTraverseState
                node.prev = startingNode
                node.steps = 1
                nodesToCheck.push(node)
            }
        })

        let allowDeallocation = true
        const stepsToCenterForEachNode: number[] = []

        nodesToCheck.forEach((checkMe) => {
            const timeoutBase = 500
            let timeout = timeoutBase
            let centerFound = false
            let minimumStepsToCenter = timeout

            const traversedNodeIds: number[] = []

            const queue: SkillNodeTraverseState[] = []
            queue.push(checkMe)

            while (queue.length > 0 && timeout > 0 && !centerFound) {
                const currentNode = queue.shift()
    
                traversedNodeIds.push(currentNode.skillId)
                
                currentNode.links.forEach(link => {
                    if (self.permanentNodeIds.includes(link.skillId)) {
                        centerFound = true
                        minimumStepsToCenter = Math.min(minimumStepsToCenter, currentNode.steps)
                        return
                    }
    
                    if(link.skillId !== currentNode.prev.skillId
                            && allocatedNodeIds.includes(link.skillId)
                            && !traversedNodeIds.includes(link.skillId)
                            ) {
                        const node = Object.assign({}, link) as SkillNodeTraverseState
                        node.prev = currentNode
                        node.steps = currentNode.steps + 1
                        queue.push(node)
                    }
                })
    
                timeout--
            }

            if (!centerFound) {
                allowDeallocation = false
            }
            if (minimumStepsToCenter !== timeoutBase) {
                stepsToCenterForEachNode.push(minimumStepsToCenter)
            }
        })

        // I'm not sure if all four of these conditions can occur, but I'm leaving it.
        if (allowDeallocation && stepsToCenterForEachNode.length > 0) {
            return {allow: true, steps: stepsToCenterForEachNode.sort()[0] }
        } else if (allowDeallocation && stepsToCenterForEachNode.length === 0) {
            return {allow: true, steps: -1}
        } else if (stepsToCenterForEachNode.length > 0) {
            return {allow: false, steps: stepsToCenterForEachNode.sort()[0]}
        } else {
            return {allow: false, steps: -1}
        }
    }

    hasNoAllocatedLinks(skillNode: SkillNode, allocatedNodeIds: number[]) {
        let allocatedLinkFound = false

        skillNode.links.forEach(link => allocatedLinkFound = allocatedLinkFound || allocatedNodeIds.includes(link.skillId))

        return !allocatedLinkFound
    }
}

export default SkillBoardTraverser